import { useState, useEffect, SyntheticEvent } from 'react';
import styled from 'styled-components';
import { capitalize } from 'underscore.string';
import { Collapse } from 'reactstrap';
import i18n from 'src/utils/translate';
import { CurrentUser, ExtendedMember } from 'src/constants/types';
import MemberCardTags from 'src/components/memberCardTags';
import Avatar, { Size } from 'src/components/avatar';
import Badge from 'src/components/badge';
import { Card } from 'src/modules/home/explore/cards/sharedStyles';
import { titleSmall, textSmallLight, text, textLinkPrimary } from 'styles-js/mixins/typography';
import { fadeEllipsis } from 'styles-js/mixins/fade';
import Button from 'src/components/buttons/button';
import { LinkButton } from 'styles-js/buttons';
import FollowButton from 'src/components/buttons/followButton';
import { Section, Element } from 'src/constants/footfall';

type MemberCardProps = {
  member: ExtendedMember;
  currentUser?: CurrentUser;
  isExpandable?: boolean;
  isFollowButton?: boolean;
  isSimplified?: boolean;
  isLink?: boolean;
  isAuthorEmptyProfile?: boolean;
  isUserLoggedIn?: boolean;
  onClick?: (href: string, username: string) => void;
  onDropdownClick?: () => void;
  onFollowClick?: () => void;
  onLinkClick?: (type?: string) => void;
}

export default function MemberCard({
  currentUser = null,
  member: {
    age, bio, country, gender, healthInterests, profileImageHash, profileImageSmall2x, roles, username, isFollowedByCurrentUser, isUserFollowsEnabled, userId,
  },
  isAuthorEmptyProfile = false,
  isExpandable = false,
  isFollowButton = false,
  isLink = false,
  isSimplified = false,
  isUserLoggedIn = true,
  onClick,
  onDropdownClick,
  onFollowClick,
  onLinkClick,
}: MemberCardProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(!isExpandable);
  }, [isExpandable]);

  const toggleCardExpand = () => {
    setIsOpen(!isOpen);
    if (onDropdownClick) onDropdownClick();
  };

  const clickCard = (href: string) => {
    if (onClick) onClick(href, username);
  };

  return (
    <Container
      as={isLink ? 'a' : 'div'}
      href={isLink ? `/user/${username}` : '#'}
      onClick={() => isLink ? clickCard(`/user/${username}`) : null}
    >
      <StyledCard isExpandable={isExpandable || isAuthorEmptyProfile || !isUserLoggedIn} isFollowButton={isFollowButton}>
        <User>
          <Link
            as={isLink || !isUserLoggedIn ? 'div' : 'a'}
            href={(isLink || !isUserLoggedIn) ? '#' : `/user/${username}`}
            onClick={() => isUserLoggedIn ? clickCard(`/user/${username}`) : null}
          >
            <AvatarWrapper>
              {profileImageSmall2x
                ? <Avatar name={username} size={Size.Medium} url={profileImageSmall2x} />
                : <Avatar hash={profileImageHash} name={username} size={Size.Medium} />
              }
            </AvatarWrapper>
            <UsernameWrapper>
              <Username data-testid="member-card-username">{username}</Username>
              {!isSimplified && roles && roles.length > 0 && (
                <div>
                  <Badge className="community-member-card__badge">{roles[0]}</Badge>
                </div>
              )}
            </UsernameWrapper>
          </Link>
          {isExpandable && (bio?.length > 0 || healthInterests?.length > 0) && (
            <ToggleButton
              ariaLabel={`View more info on ${username}`}
              icon={isOpen ? 'open-up' : 'open-down'}
              type="none"
              onClick={toggleCardExpand}
            />
          )}
        </User>
        {(isUserLoggedIn && !isAuthorEmptyProfile) && (!isExpandable || (gender || age || country)) && (
          <Info>
            {gender && <span>{capitalize(gender)}</span>}
            {age && <span>{!isSimplified ? `${age} ${i18n.t('years old')}` : `${age}`}</span>}
            {country && <span>{country}</span>}
          </Info>
        )}
        {!isUserLoggedIn || isAuthorEmptyProfile ? (
          <Collapse isOpen={isOpen}>
            {!isUserLoggedIn ? (
              <Message>{i18n.t('To view profiles and participate in discussions please ')}
                <LinkButton onClick={() => { if (onLinkClick) onLinkClick('join'); }}>{i18n.t('join')}</LinkButton>{i18n.t(' or ')}
                <LinkButton onClick={() => { if (onLinkClick) onLinkClick('login'); }}>{i18n.t('log in')}</LinkButton>.
              </Message>
            ) : (
              <Message>{i18n.t('Help members answer you by giving more ')}
                <LinkSpan onClick={() => { if (onLinkClick) onLinkClick(); }}>{i18n.t('context on your profile')}</LinkSpan>
              </Message>
            )}
          </Collapse>
        ) : (
          <Collapse isOpen={isOpen}>
            {((isExpandable && bio?.length > 0) || !isExpandable) &&
              <Bio>{bio}</Bio>}
            {(healthInterests?.length > 0 || isFollowButton) && (
              <MemberCardTags
                hasLink={isLink || isExpandable}
                tags={healthInterests}
                username={username}
                onMoreClick={onClick}
              />
            )}
          </Collapse>
        )}
        {!!currentUser && isFollowButton && (
          <>
            <StyledFollowButton
              buttonText={isFollowedByCurrentUser ? 'Following' : 'Follow'}
              buttonType={isFollowedByCurrentUser ? 'secondary' : 'primary'}
              followIds={[userId]}
              followType="user"
              isDisabled={currentUser.userId === userId || !isUserFollowsEnabled}
              isFollowing={isFollowedByCurrentUser}
              noIcon={isFollowedByCurrentUser ? false : true}
              signupContext={{
                element: Element.FOLLOW,
                section: Section.PUBLIC_PROFILE,
              }}
              trackLabel={username}
              onFollowClick={onFollowClick}
            />
          </>
        )}
      </StyledCard>
    </Container>
  );
}

type LinkProps = {
  as?: React.ElementType;
  href?: string;
  onClick: (event: SyntheticEvent) => void;
}
const Container = styled.a<LinkProps>`
  display: block;
`;
const Link = styled.a<LinkProps>`
  display: flex;
  flex-grow: 2;
  overflow: hidden;
`;
const LinkSpan = styled.span<LinkProps>`
  ${textLinkPrimary};
`;
const StyledCard = styled(Card)<{ isExpandable?: boolean, isFollowButton?: boolean }>`
  height: ${props => !props.isExpandable ? (props.isFollowButton ? '270px' : '235px') : 'auto'};
  max-width: ${props => !props.isExpandable ? '366px' : 'auto'};
  margin: ${props => !props.isExpandable ? '14px 3px' : '14px 0px'};
  overflow: hidden;
  @media (${({ theme }) => theme.underScreenSmall}) {
    height: ${props => !props.isExpandable ? (props.isFollowButton ? '268px' : '235px') : 'auto'};
  }
`;
const User = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
  margin-bottom: 14px;
`;
const AvatarWrapper = styled.div`
  img {
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }
`;
const Username = styled.span`
  ${titleSmall};
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const UsernameWrapper = styled.div`
  overflow: hidden;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;
const Info = styled.div`
  ${textSmallLight};
  height: ${({ theme }) => theme.fontLineHeightXs};
  margin-bottom: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  span:not(:last-child)::after {
    content: "•";
    color: ${({ theme }) => theme.colorGreyLight};
    margin: 0 4px;
  }
`;
const Bio = styled.p`
  ${text}
  height: 42px;
  position: relative;
  overflow: hidden;
  margin-bottom: 14px;
  ::after {
    ${fadeEllipsis};
  }
`;
const Message = styled.div`
  position: relative;
  overflow: hidden;
  color: ${({ theme }) => theme.colorTextLight};
`;
const ToggleButton = styled(Button)`
  color: ${({ theme }) => theme.colorTextLinkPrimary};
  &:hover {
    color: ${({ theme }) => theme.colorTextLinkPrimaryHover};
  }
`;
const StyledFollowButton = styled(FollowButton)`
  margin: 5px 0 0;
`;
